import { useExpertRules } from './expertRulesStore';
import { CallsBadge, type CallEntry } from './CallsBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { useAssemblySteps } from './categoryBands';
import { useCategories } from './catalogCategories';
import { STEP_PARTS_MODEL, STEPS_MODEL } from './models';
import { usePartsRun } from './partsRun';
import { STEPS_PHASE, askStopElsewhere, useWorkingElsewhere } from './phaseWorking';
import { haltGate, stopRuns, useStopAsked } from './runHalt';
import { buildStepAnalyzePrompt } from './stepAnalyzePrompt';
import { buildStepPartsPrompt } from './stepPartsPrompt';
import {
  getStepParts, getStepText, runStepPartsPhase, stepPartsPictures, useStepParts,
} from './stepPartsRun';
import { buildStepInstructionsPrompt } from './stepInstructionsPrompt';
import { stepInstructionsPictures, useStepInstructions } from './stepInstructionsRun';
import { useStepSections } from './stepSections';
import {
  buildStepPromptContext, extractSteps, nextPieceNo, piecesBefore, stepAssemblyInputs,
  useStepsRun,
} from './stepsRun';
import { useSince } from './waitClock';
import { useLanguage } from '../../contexts/LanguageContext';

// PHASE 4'S ONE BADGE: every call the steps phase makes, walked in the order they run — the
// overview's two (the parts and tools shared out among the steps, and each step's printed
// text), then the analysis, one call per step. It was two badges, one per half.
//
// ITS RUN IS A CHOICE OF THREE, because the halves depend on each other in one direction only:
// the analysis is given the overview. Everything runs both in that order; the overview alone
// is a quick re-share that leaves the sub-steps as they are; the analysis alone re-reads every
// step against the overview already there. No single step: each step's pieces are numbered on
// from the step before, and a step re-read alone is the one thing a whole run gets right that
// it can only approximate.
//
// THE HARDWARE TABLE IS NOT RUN FROM HERE. The analysis used to set it off when it finished; it
// is Phase 5's own now, run from its own badge (or by Run all), so a press here spends nothing
// in a phase that is not on screen.
export function StepsBadge({ docId, file, locked }: { docId: string; file: Blob; locked?: boolean }) {
  const { t } = useLanguage();
  // SUBSCRIBED, so the prompt shown follows the expert rules as they are edited (see expertRulesStore).
  useExpertRules();
  const { model, steps } = useAssemblySteps(docId);
  const { items, busy: partsBusy } = usePartsRun(docId);
  const alloc = useStepParts(docId);
  const said = useStepInstructions(docId);
  const st = useStepsRun(docId);
  const sections = useStepSections(file, steps);
  // THE ONTOLOGY, SUBSCRIBED: the prompts' part-kind questions go through the module cache,
  // which answers "component" for everything until the fetch lands.
  useCategories();

  // THE ANALYSIS RUNNING SOMEWHERE ELSE — another tab, another admin. The manual is shared and
  // it is the long run, so the button that offers to do the work has to know when the work is
  // already being done: two readers pressing it both pay for every step. See phaseWorking.
  const elsewhere = useWorkingElsewhere(docId);
  const away = !st.busy && elsewhere?.phase === STEPS_PHASE ? elsewhere : null;
  const overviewBusy = alloc.busy || said.busy;
  const busy = overviewBusy || st.busy;
  const progressSuffix = st.busy && st.progress
    ? ` · ${st.progress.done}/${st.progress.total}`
    : away ? ` · ${away.done}/${away.total}` : '';
  // The analysis keeps its own clock (see stepsRun), which survives a reload mid-run; the
  // overview is timed by the badge itself.
  const running = useSince(st.startedAt);
  const took = st.startedAt != null ? running : st.elapsed;

  const runOverview = () => runStepPartsPhase(docId, file, steps, items, t);
  // The overview this manual has NOW — read at the moment of the call, so an Everything press
  // hands the analysis the answer that just landed rather than the one on screen when pressed.
  const runAnalysis = () => {
    if (!model || !steps.length) return Promise.resolve();
    return extractSteps(
      docId, file, steps, items, getStepParts(docId), getStepText(docId), STEPS_MODEL, t,
    );
  };
  const runEverything = () => {
    const halted = haltGate(docId);
    void runOverview().then(() => {
      if (!halted()) void runAnalysis();
    });
  };

  // One step's analysis prompt, from the SAME builders the run uses, so it is byte-for-byte
  // what step-analyze is sent for that step.
  const analysisPrompt = (at: number) => {
    const step = steps[at];
    const { assembled, previousSteps } =
      stepAssemblyInputs(steps, alloc.byStep, alloc.text, items, at);
    return buildStepAnalyzePrompt(
      buildStepPromptContext(
        items,
        {
          parts: alloc.byStep[step.category] ?? [],
          text: alloc.text[step.category]?.en ?? '',
          // THE OVERVIEW'S TOOLS FOR THIS STEP, with the steps store as the fallback for a
          // manual analysed while the analysis was the one asking.
          tools: alloc.tools[step.category] ?? st.tools[step.category] ?? [],
        },
        assembled,
        previousSteps,
        piecesBefore(steps, st.subSteps, at),
      ),
      !assembled.length,
      nextPieceNo(steps, st.subSteps, at),
    );
  };

  const allocDone = Object.keys(alloc.byStep).length > 0;
  const entries: CallEntry[] = [
    {
      key: 'share',
      label: t('notice.parts.callShare'),
      load: async () => ({
        prompt: buildStepPartsPrompt(items),
        model: STEP_PARTS_MODEL,
        urls: await stepPartsPictures(file, steps, t),
      }),
      answer: allocDone ? reasoningTooltip(alloc.reasoning, alloc.raw, t) : undefined,
    },
    {
      key: 'text',
      label: t('notice.parts.instrTitle'),
      load: async () => ({
        prompt: buildStepInstructionsPrompt(steps),
        model: STEP_PARTS_MODEL,
        urls: await stepInstructionsPictures(file, steps, t),
      }),
      answer: said.raw || said.reasoning ? reasoningTooltip(said.reasoning, said.raw, t) : undefined,
    },
    // ONE PER STEP, the analysis. Its "raw output" is the answer as the phase kept it — what the
    // step builds on and its sub-steps. Not the model's own bytes, which the server parses and
    // does not return: what is shown is what the phase works from.
    ...steps.map((step, at): CallEntry => {
      const subs = st.subSteps[step.category] ?? [];
      return {
        key: `analyse-${step.category}`,
        label: t('notice.steps.promptLabel').replace('{n}', String(step.num)),
        load: () => Promise.resolve({
          prompt: analysisPrompt(at),
          urls: sections[step.category] ?? [],
          model: STEPS_MODEL,
        }),
        answer: subs.length ? reasoningTooltip(
          st.reasoning[step.category] ?? '',
          JSON.stringify({ buildsOn: st.buildsOn[step.category] ?? [], subSteps: subs }, null, 2),
          t,
          st.timing[step.category] ?? null,
        ) : undefined,
      };
    }),
  ];

  const stopAsked = useStopAsked(docId, busy);
  if (!steps.length) return null;

  const off = !!locked || partsBusy || !!away || !items.length;
  return (
    <CallsBadge
      title={t('notice.merged.title')}
      named
      entries={entries}
      run={{
        label: `${away ? t('notice.steps.runElsewhere') : t('notice.steps.runSteps')}${progressSuffix}`,
        onRun: runEverything,
        busy: busy || !!away,
        disabled: off,
        // STOPS THIS TAB'S RUN, between steps: the step out lands and is kept. OR ONE GOING ON IN
        // ANOTHER TAB, by asking it to (see askStopElsewhere): it stops at its next step.
        onStop: locked ? undefined : busy ? () => stopRuns(docId) : away ? () => askStopElsewhere(docId) : undefined,
        // SAID AT ONCE: the calls out are being cancelled, which takes a poll or two.
        ...(stopAsked ? { stopping: t('notice.stop.asked') } : {}),
        // The analysis's own clock while it is the half running or the last to have run.
        elapsed: overviewBusy ? undefined : took,
        options: [
          { key: 'all', label: t('notice.steps.runEverything'), onRun: runEverything },
          { key: 'overview', label: t('notice.steps.runOverview'), onRun: () => void runOverview() },
          {
            key: 'analysis',
            label: t('notice.steps.runAnalysis'),
            onRun: () => {
              if (allocDone) void runAnalysis();
            },
          },
        ],
      }}
    />
  );
}
